import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    DEFAULT_ROOM_DISPLAY_NAME,
    setStoredRoomDisplayName,
} from '../roomDisplayName';

type EditableRoomDisplayNameProps = {
    value: string;
    onSave: (name: string) => void;
    editable: boolean;
    className?: string;
    inputClassName?: string;
    /** e.g. "text-3xl font-medium ..." for lobby title */
    as?: 'h3' | 'span' | 'div';
};

/**
 * Double-click to edit; Enter or blur saves via onSave (caller should persist + WS).
 */
export const EditableRoomDisplayName: React.FC<EditableRoomDisplayNameProps> = ({
    value,
    onSave,
    editable,
    className = '',
    inputClassName = '',
    as: Tag = 'span',
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!editing) setDraft(value);
    }, [value, editing]);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    const commit = useCallback(() => {
        const next = draft.trim().slice(0, 120) || DEFAULT_ROOM_DISPLAY_NAME;
        setStoredRoomDisplayName(next);
        onSave(next);
        setEditing(false);
    }, [draft, onSave]);

    const cancel = useCallback(() => {
        setDraft(value);
        setEditing(false);
    }, [value]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={onKeyDown}
                className={inputClassName}
                maxLength={120}
                aria-label="Room name"
            />
        );
    }

    return (
        <Tag
            className={`${className} ${editable ? 'cursor-text select-none' : ''}`}
            onDoubleClick={
                editable
                    ? (e) => {
                          e.preventDefault();
                          setEditing(true);
                      }
                    : undefined
            }
            title={editable ? 'Double-click to edit' : undefined}
        >
            {value || DEFAULT_ROOM_DISPLAY_NAME}
        </Tag>
    );
};
